import React from 'react'
import {
    Box,
    BoxProps,
    Button,
    Card,
    CardProps,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    LinearProgress,
    MenuItem,
    Paper,
    Select,
    SelectChangeEvent,
    Table,
    TableBody,
    TableCell,
    TableCellProps,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    TextFieldProps,
    Typography,
    TypographyProps,
    useTheme,
} from '@mui/material'
import * as singUserService from 'services/signuser-service';
import dayjs, { Dayjs } from 'dayjs';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import * as Styles from 'pages/development/settings/tabs/signinUsers/signinUsers.styles';
import * as StylesSettings from 'pages/development/settings/Settings.styles';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import nlBeLocale from "dayjs/locale/nl-be";
import i18next from 'i18next';
import 'dayjs/locale/nl';
import 'dayjs/locale/fr';
import 'dayjs/locale/en';
import { IPropsSettings } from '../../Settings';

interface IError {
    error: string;
    isError: boolean;
}

interface IDateError {
    from: IError;
    to: IError;
}

const localeMap = {
    en: { name: undefined, format: "DD/MM/YYYY", placeholder: "dd/mm/yyyy" },
    nl: { name: nlBeLocale, format: "DD/MM/YYYY", placeholder: "dd/mm/jjjj" },
    fr: { name: undefined, format: "DD/MM/YYYY", placeholder: "dd/mm/jjjj" }
};

const SigninUsers = (props: IPropsSettings) => {
    const { tenantId } = props;
    const { t, i18n: { language } } = useTranslation();
    const theme = useTheme();
    const [signUser, setSignUser] = React.useState<SignUser[]>([]);
    const [dateFrom, setDateFrom] = React.useState<Dayjs | null>(null);
    const [dateTo, setDateTo] = React.useState<Dayjs | null>(null);
    const [error, setError] = React.useState<IError>({ error: '', isError: false });
    const [dateError, setDateError] = React.useState<IDateError>({ from: { error: '', isError: false }, to: { error: '', isError: false } });
    const [names, setNames] = React.useState<SignerNameDto[]>([]);
    const [name, setName] = React.useState<string>('');
    const [employeeId, setEmployeeId] = React.useState<string>('');
    const [locale] = React.useState<keyof typeof localeMap>(
        i18next.language as any
    );
    const [loading, setLoading] = React.useState<boolean>(false);

    React.useEffect(() => {
        setLoading(true);
        singUserService.getUsers(tenantId).then((res: SignUser[]) => {
            setSignUser(res.map((user: SignUser) => { return { name: user.name, dateFrom: dayjs(user.dateFrom), dateTo: dayjs(user.dateTo), id: user.id, employeePoliceId: user.employeePoliceId } }));
        })
        singUserService.getNames(tenantId).then((res: SignerNameDto[]) => {
            setNames(res);
            setLoading(false);
        });
    }, [tenantId]);

    const handleAddVacation = (): void => {
        if (dateFrom && dateTo && name && dateFrom.isBefore(dateTo)) {
            singUserService.addSingUsers(dateFrom, dateTo, name, employeeId, tenantId).then((res: SignUser) => {
                setSignUser([...signUser, { name: res.name, dateFrom: dayjs(res.dateFrom), dateTo: dayjs(res.dateTo), id: res.id, employeePoliceId: res.employeePoliceId }]);
                setDateFrom(null);
                setDateTo(null);
                setName('');
                setError({ error: '', isError: false });
            })
                .catch((err: AxiosError<any>) => {
                    setError({ error: err.response?.data.error.message as string, isError: true });
                });
        } else if (dateFrom?.isAfter(dateTo)) {
            setError({ error: 'settings.signers.errorBefore', isError: true });
        } else {
            setError({ error: 'settings.signers.errorFill', isError: true });
        }
    }

    const handleDetele = (id: string): void => {
        singUserService.deleteSignUser(id, tenantId).then(() => {
            setSignUser(signUser.filter((user: SignUser) => user.id !== id));
        });
    }

    const handleNameChange = (event: SelectChangeEvent): void => {
        const {
            target: { value },
        } = event;
        const name = names.find((name: SignerNameDto) => name.employeePoliceId === value)?.name as string
        console.log(name)
        setName(name);
        setEmployeeId(value as string);
    }

    const listCertificatesWrapperProps: CardProps = {
        sx: Styles.listCertificatesWrapper(theme)
    }

    const titleWrapperProps: BoxProps = {
        sx: Styles.titleWrapper(theme)
    }

    const dateFromTextFieldProps = (params: TextFieldProps): TextFieldProps => {
        return {
            ...params,
            // helperText: dateError.from.isError ? t(dateError.from.error) : '',
            // error: dateError.from.isError,
            inputProps: {
                ...params.inputProps,
                placeholder: localeMap[locale].placeholder
            },
            size: 'small',
            sx: {
                width: '75%'
            }
        }
    }

    const dateToTextFieldProps = (params: TextFieldProps): TextFieldProps => {
        return {
            ...params,
            // helperText: dateError.to.isError ? t(dateError.to.error) : '',
            // error: dateError.to.isError,
            inputProps: {
                ...params.inputProps,
                placeholder: localeMap[locale].placeholder
            },
            size: 'small',
            sx: {
                width: '75%'
            }
        }
    }

    const handleDateToValue = (): Dayjs => {
        if (dateFrom !== null && dateFrom !== undefined) {
            const date = dayjs(dateFrom).add(1, 'day');
            return date;
        }
        return dayjs();
    }

    const handleChange = (value: Dayjs | null, type: 'from' | 'to', input: string | undefined): void => {
        const error: IError = { error: '', isError: false };
        const date: Dayjs = dayjs(value);
        if (input !== undefined && input.length >= 10 && !date.isValid()) {
            error.isError = true;
            error.error = 'yup.step3.dateError';
        }
        switch (type) {
            case 'from':
                if (value !== null && value !== undefined) {
                    setDateFrom(value);
                    setDateError({ ...dateError, from: error });
                }
                break;
            case 'to':
                if (date.isBefore(dayjs(dateFrom))) {
                    error.isError = true;
                    error.error = 'yup.step3.minDate';
                }
                if (value !== null && value !== undefined) {
                    setDateTo(value);
                    setDateError({ ...dateError, to: error });
                }
                break;
        }
    }

    const tableHeaderCellProps: TableCellProps = {
        sx: Styles.tableHeaderCell(theme),
        width: '25%',
        align: 'center',
    }

    const errorProps: TableCellProps = {
        sx: Styles.error(theme),
        width: '25%',
        align: 'center',
    }

    return (
        <Box>
            <Typography variant='h4'>
                {t('settings.signers.title')}
            </Typography>
            <Box>
                {t('settings.signers.description')}
            </Box>
            {loading && (
                <Box sx={{ marginTop: '1rem' }}>
                    <LinearProgress/>
                </Box>
            )}
            {!loading && (
                <React.Fragment>
                    <Box sx={{marginTop: "1rem"}}>
                        <Grid container spacing={2} sx={ StylesSettings.infoWrapper(theme) }>
                            <Grid item xs={12} md={3}>
                                <Box>
                                    {t('settings.signers.signers')}
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={9}>
                                <TextField multiline maxRows={5} helperText={t('settings.signers.descriptionSigners')} disabled value={names.map((el) => el.name).join('\n')} size='small' />
                            </Grid>
                        </Grid>
                    </Box>
                    <Card {...listCertificatesWrapperProps}>
                        <Box {...titleWrapperProps}>
                            {error.isError &&
                                <Box color="error.main">
                                    <Box>{t(error.error)}</Box>
                                </Box>
                            }
                        </Box>
                        <Box>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell {...tableHeaderCellProps}>
                                                {t('settings.signers.name')}
                                            </TableCell>
                                            <TableCell {...tableHeaderCellProps}>
                                                {t('settings.signers.dateFrom')}
                                            </TableCell>
                                            <TableCell {...tableHeaderCellProps}>
                                                {t('settings.signers.dateTo')}
                                            </TableCell>
                                            <TableCell {...tableHeaderCellProps}>
                                                {t('settings.signers.delete')}
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                </Table>
                                <Box sx={{ overflow: 'auto', height: 'calc(100vh - (84px * 1) - 2px - 180px)' }}>
                                    <Table sx={{ minWidth: 650, tableLayout: 'fixed' }} aria-label="Vacations table">
                                        <TableBody>
                                            {signUser.map((row: SignUser) => (
                                                <TableRow key={row.id}>
                                                    <TableCell align='center' component="th" scope="row">
                                                        {row.name}
                                                    </TableCell>
                                                    <TableCell align='center'>
                                                        {row.dateFrom.format('DD/MM/YYYY')}
                                                    </TableCell>
                                                    <TableCell align='center'>{row.dateTo.format('DD/MM/YYYY')}</TableCell>
                                                    <TableCell align='center'>
                                                        <IconButton
                                                            onClick={() => handleDetele(row.id)}
                                                            children={<DeleteIcon />}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                            <TableRow>
                                                <TableCell align='center'>
                                                    <FormControl sx={{ width: '75%' }}>
                                                        <InputLabel id='name' size='small'>{t('settings.signers.name')}</InputLabel>
                                                        <Select
                                                            size='small'
                                                            labelId='name'
                                                            value={employeeId}
                                                            label={t('settings.signers.name')}
                                                            onChange={handleNameChange}
                                                        >
                                                            {names.map((name: SignerNameDto) => <MenuItem key={name.employeePoliceId} value={name.employeePoliceId}>{name.name}</MenuItem>)}
                                                        </Select>
                                                    </FormControl>
                                                </TableCell>
                                                <TableCell align='center'>
                                                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={language} >
                                                        <DatePicker
                                                            onChange={(day: Dayjs | null, input) => handleChange(day, 'from', input)}
                                                            inputFormat={localeMap[locale].format}
                                                            value={dateFrom}
                                                            renderInput={(params: TextFieldProps) => <TextField {...dateFromTextFieldProps(params)} />}
                                                        />
                                                    </LocalizationProvider>
                                                </TableCell>
                                                <TableCell align='center'>
                                                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={language} >
                                                        <DatePicker
                                                            onChange={(day: Dayjs | null, input) => handleChange(day, 'to', input)}
                                                            minDate={handleDateToValue()}
                                                            showDaysOutsideCurrentMonth
                                                            value={dateTo}
                                                            renderInput={(params: TextFieldProps) => <TextField {...dateToTextFieldProps(params)} />}
                                                            inputFormat="DD/MM/YYYY"
                                                            defaultCalendarMonth={dateFrom?.add(1, 'day')}
                                                        />
                                                    </LocalizationProvider>
                                                </TableCell>
                                                <TableCell align='center'>
                                                    <Button variant='contained' onClick={handleAddVacation}>{t('settings.signers.add')}</Button>
                                                </TableCell>
                                            </TableRow>
                                            {(dateError.from.isError || dateError.to.isError) &&
                                                <TableRow>
                                                    <TableCell align='center'>
                                                    </TableCell>
                                                    <TableCell {...errorProps}>
                                                        {t(dateError.from.error)}
                                                    </TableCell>
                                                    <TableCell {...errorProps}>
                                                        {t(dateError.to.error)}
                                                    </TableCell>
                                                    <TableCell align='center'>
                                                    </TableCell>
                                                </TableRow>
                                            }
                                        </TableBody>
                                    </Table>
                                </Box>
                            </TableContainer>
                        </Box>
                    </Card>
                </React.Fragment>
            )}
        </Box>
    )
}

export default SigninUsers;
